<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  evaluationCrewList,
  evaluationCrewCreate,
  evaluationCrewDelete,
} from "@/common/api/rest/evaluation/crew";

import type { EvaluationType } from "@/common/types/evaluation";
import type { EvaluationCrewListType } from "@/common/types/evaluation/crew";

import { searchCrew } from "@/components";
import { baseConfirm, baseModal, baseTable } from "@/common/components";

interface SelectCrewType {
  member_id: string;
  name: string;
}

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    isEdit: true,
  },
);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
]);

const tableData = ref<EvaluationCrewListType[]>([]);

const selected = ref<EvaluationCrewListType>(null);
const selectedCrew = ref<EvaluationCrewListType[]>([]);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showConfirmDel = ref(false);

function fetchData() {
  if (!props.data) return;

  evaluationCrewList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function add() {
  showModalAdd.value = true;
  selectedCrew.value = [];
}

function addCancel() {
  selectedCrew.value = [];
  showModalAdd.value = false;
}

function addSelect(data: EvaluationCrewListType[]) {
  selectedCrew.value = data;
}

function addConfirm() {
  if (!props.data || !selectedCrew.value.length) return;

  showModalAdd.value = false;

  const promises = selectedCrew.value.map((sl) =>
    evaluationCrewCreate(props.data.account_id, {
      evaluation_id: props.data.evaluation_id,
      member_id: sl.member_id,
      name: sl.name,
      type: sl.type,
    }),
  );

  Promise.all(promises)
    .then((responses) => {
      responses.forEach((response) => {
        const { success, message } = response;
        if (success) {
          toast({ message: message, type: "success" });
          update();
          fetchData();
        } else {
          toast({ message: message, type: "error" });
        }
      });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function remove(data: EvaluationCrewListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);

  evaluationCrewDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        update();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="showTitle">
      <div class="title">
        {{ t("common.labels.crew") }}
      </div>
    </template>

    <div class="content">
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :add="isEdit"
        :del="isEdit"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:del="remove"
      >
        <template #name="{ data }">
          <span> {{ data.name }} </span>
        </template>
        <template #type="{ data }">
          <span>
            {{ t(`technical.types.${data.type}`) }}
          </span>
        </template>
      </baseTable>
    </div>

    <base-confirm
      :show="showConfirmDel"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.technical'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: selected?.name,
        })
      "
      @cancel="removeCancel"
      @confirm="removeConfirm"
    />

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.technical'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-crew
          :account_id="data?.account_id"
          :ids="tableData?.map((item) => item.member_id)"
          @update-data="addSelect"
        />
      </template>
    </base-modal>
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }
}
</style>
